import { deriveBoostProfile } from "./boost-profile";
import type { ProfileCopyPack } from "./profile-copy";
import type { Answers, TSuppressionProfile } from "./_schema";

/**
 * Spanish (US) verdict prose for quiz-v6-es. Copy-only: it rides the
 * `opts.copy` seam on `deriveBoostProfile`, so every enum field the deriver
 * produces (suppressionLevel, declinePattern, qualityOfLifeImpact,
 * rootCauses, evidence, expectedOutcome) is unchanged -- only `assessment`'s
 * three strings differ from the English pack.
 *
 * Approved copy lives in docs/specs/2026-07-28-quiz-v6-es-design.md ("Verdict
 * card" and "Verdict explanations (Tier 2)" sections) -- keep the two in
 * sync. Transcribed verbatim; do not reword.
 *
 * Register: neutral Latin American Spanish, `tú` (informal). See the spec's
 * "Terminology decisions" table for word-choice rationale.
 */
export const ES_PROFILE_COPY: ProfileCopyPack = {
  assessmentLabel: {
    low: "ESTABLE",
    normal: "DECLIVE TEMPRANO",
    medium: "EN DECLIVE",
    high: "SUPRIMIDA",
  },
  assessmentHeadline: {
    low: "Tu Evaluación de Testosterona: ESTABLE",
    normal: "Tu Evaluación de Testosterona: DECLIVE TEMPRANO",
    medium: "Tu Evaluación de Testosterona: EN DECLIVE",
    high: "Tu Evaluación de Testosterona: SUPRIMIDA",
  },
  explanation: {
    "cortisol-dominance": {
      stronger:
        "Según tus respuestas, tu producción de testosterona está siendo suprimida activamente por las hormonas del estrés crónico.",
      softer:
        "Tus respuestas muestran que las hormonas del estrés ya están empezando a interferir con tu producción de testosterona.",
    },
    "chronic-stress": {
      stronger:
        "El estrés sostenido está anulando tu ciclo de recuperación hormonal. Tu cuerpo está en modo defensa, no en modo reconstrucción.",
      softer:
        "Tus patrones de estrés están empezando a mover tu balance hormonal en contra de la testosterona.",
    },
    "sleep-deprivation": {
      stronger:
        "Tu ventana de sueño es demasiado corta para que el ciclo de producción de testosterona se complete. La mayor parte de la T se libera durante el sueño profundo, y te la estás perdiendo.",
      softer:
        "Tu ventana de sueño es lo bastante corta para reducir tu producción nocturna de testosterona.",
    },
    "visceral-adiposity": {
      stronger:
        "Tu composición corporal está convirtiendo tu testosterona en estrógeno por la vía de la aromatasa, creando un círculo que se refuerza a sí mismo.",
      softer:
        "El tejido adiposo ya está empezando a convertir testosterona en estrógeno, frenando tu recuperación hormonal.",
    },
    "metabolic-syndrome": {
      stronger:
        "Las señales de insulina e inflamación están suprimiendo directamente la producción de testosterona en las células de Leydig.",
      softer: "Tus marcadores metabólicos apuntan a una alteración hormonal temprana.",
    },
    "lifestyle-load": {
      stronger:
        "Varias presiones de tu estilo de vida se están acumulando una sobre otra. No hay una sola solución: tu sistema necesita un reinicio.",
      softer:
        "Varios factores de tu estilo de vida están dejando tu producción hormonal por debajo de donde debería estar.",
    },
    "age-related-decline": {
      stronger:
        "El declive de las células de Leydig por edad se está sumando a otros supresores. La caída es real, pero la velocidad no es tu destino.",
      softer:
        "Tu cuerpo ya empezó el declive natural por edad. La pendiente es más pronunciada de lo que tendría que ser.",
    },
    overtraining: {
      stronger:
        "Tu carga de entrenamiento va más rápido que tu recuperación. Tu relación testosterona-cortisol está de cabeza.",
      softer:
        "Tu carga de entrenamiento está empezando a superar tu recuperación, suprimiendo la señalización de testosterona.",
    },
    "endocrine-disruption": {
      stronger:
        "La exposición a químicos de los alimentos procesados y sus empaques está creando una carga constante de fondo sobre tu sistema endocrino.",
      softer:
        "La exposición de fondo a disruptores endocrinos está añadiendo una presión baja pero constante a tu sistema hormonal.",
    },
    "catabolic-deficit": {
      stronger:
        "La pérdida rápida de peso está poniendo a tu cuerpo en modo crisis: baja tu testosterona y descompone músculo para usarlo como combustible. La grasa se va, pero también la fuerza que estaba debajo.",
      softer:
        "La pérdida rápida de peso ya está empezando a bajar tu testosterona, así que parte de lo que estás perdiendo es músculo, no solo grasa.",
    },
    "blocked-testosterone": {
      stronger:
        "Tu testosterona no está ausente: está siendo bloqueada antes de que tu cuerpo pueda usarla. La SHBG se une a lo que produces, y demasiado se convierte en estrógeno primero.",
      softer:
        "Tu cuerpo sigue produciendo testosterona, pero cada vez más se queda unida y se convierte antes de llegar a tus tejidos.",
    },
    "exogenous-suppression": {
      stronger:
        "La testosterona externa le ha indicado a tu cuerpo que deje de producir la propia. Tu producción natural quedó dormida, y esa es la dependencia que sientes. Dormida no es muerta: la vía se reactiva cuando la señal externa baja y regresan los estímulos correctos.",
      softer:
        "Agregar testosterona desde afuera le está diciendo en silencio a tu cuerpo que baje su propia producción. La vía natural sigue ahí, solo necesita la señal para volver a encenderse.",
    },
    // Present only to satisfy the exhaustive Record<RootCause, ...> type:
    // `nocturnal-cortisol` is quiz-sleep's forced primary and is never scored
    // organically, so no quiz-v6-es path can reach it. Same reason the
    // catabolic-deficit / blocked-testosterone / exogenous-suppression entries
    // above are unreachable from this quiz.
    "nocturnal-cortisol": {
      stronger:
        "Tu cortisol se dispara en las primeras horas de la mañana y recorta la ventana de sueño profundo donde se produce tu testosterona.",
      softer:
        "Tu cortisol se está calmando más tarde de lo que debería y ya empieza a recortar la ventana de sueño profundo donde se produce tu testosterona.",
    },
    // Same story as `nocturnal-cortisol` above: `estrogen-dominance` is
    // quiz-beer's forced primary, never scored organically, so no quiz-v6-es
    // path reaches it. Present for type exhaustiveness. Kept dose-agnostic for
    // the same reason as the English entry -- see quizzes/profile-copy.ts.
    "estrogen-dominance": {
      stronger:
        "Tu balance hormonal se inclinó hacia el estrógeno. Los estrógenos vegetales de la cerveza suman a una carga que tu cuerpo ya lleva.",
      softer:
        "Tu balance hormonal está empezando a inclinarse hacia el estrógeno, y cada cerveza suma a la carga que tu cuerpo ya lleva.",
    },
  },
  fallbackExplanation: {
    stronger: "Tus respuestas apuntan a un patrón hormonal que necesita atención.",
    softer: "Tus respuestas sugieren que tu señalización de testosterona está empezando a desviarse.",
  },
};

/**
 * quiz-v6-es's `deriveProfile`. Wraps `deriveBoostProfile` with the Spanish
 * copy pack and nothing else -- branching, classifier, severity scoring,
 * decline-pattern selection, root-cause ranking, and evidence all remain
 * quiz-v6's, unmodified. See `deriveBoostProfile`'s `copy` seam doc comment
 * in `./boost-profile` for the contract this relies on.
 */
export const deriveQuizV6EsProfile = (answers: Answers): TSuppressionProfile =>
  deriveBoostProfile(answers, { copy: ES_PROFILE_COPY });
